import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LoginAction, ResendActivationAction } from "../../store/action";
import { ACCESS_TOKEN } from "../../constants/commonConst";
import AuthShell from "../ui/AuthShell";
import GoogleSignIn from "../googleSignIn/GoogleSignIn";
import { usePageMeta } from "../../hooks/usePageMeta";
import Toast from "../ui/Toast";
import {
  fieldClass,
  labelClass,
  primaryButtonClass,
  textLinkClass,
} from "../ui/formStyles";

/**
 * Login page.
 *
 * e2e contract (login.spec.js): `.login-content` wraps the "Welcome Back"
 * heading, the "Email"/"Password" placeholders, the submit button, and the
 * signup link. Errors surface through an element with `role="alert"`. Those
 * strings live in the `en` bundle under `Login.*`, which is what the spec's
 * browser resolves to.
 */
const Login = ({ authenticated, onLoginSuccess }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  usePageMeta({ title: `${t("Menu.LogIn")} | EasyPlanner` });

  useEffect(() => {
    if (location.state && location.state.error) {
      setTimeout(() => {
        navigate(location.pathname, { state: {} });
      }, 100);
    }
  }, [location, navigate]);

  if (authenticated) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  return (
    <AuthShell className="login-content w-full max-w-md">
      <div className="text-center">
        <span className="inline-flex size-12 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
          <i className="bx bx-calendar-check text-2xl" aria-hidden="true" />
        </span>
        <h1 className="login-title mt-4 text-fluid-xl font-bold tracking-tight text-ink-900">
          {t("Login.title")}
        </h1>
        <p className="mt-2 text-fluid-sm text-ink-500">{t("Login.subtitle")}</p>
      </div>

      <LoginForm onLoginSuccess={onLoginSuccess} />

      <span className="signup-link mt-6 block text-center text-fluid-sm text-ink-500">
        {t("Login.newUser")}{" "}
        <Link to="/signup" className={textLinkClass}>
          {t("Login.signupLink")}
        </Link>
      </span>
    </AuthShell>
  );
};

const LoginForm = ({ onLoginSuccess }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Off by default: a remembered browser stays signed in for as long as it is
  // used, which is the wrong default on a computer several people share.
  const [rememberMe, setRememberMe] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [needsActivation, setNeedsActivation] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const navigate = useNavigate();

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === "email") setEmail(value);
    if (name === "password") setPassword(value);
  };

  const handleClose = () => setOpen(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setNeedsActivation(false);
    setResendMessage("");

    const loginRequest = { email, password, rememberMe };

    try {
      const response = await LoginAction(loginRequest);
      localStorage.setItem(ACCESS_TOKEN, response.accessToken);
      localStorage.setItem("USER_EMAIL", email);
      onLoginSuccess();
      navigate("/dashboard/organization");
    } catch (error) {
      // The credentials were right but the address was never confirmed. That is
      // a fixable state, not a failed login, so it gets an inline panel with a
      // resend button instead of a toast the user can only dismiss.
      if (error.response?.data?.code === "ACCOUNT_NOT_ACTIVATED") {
        setNeedsActivation(true);
      } else {
        setErrorMessage(
          error.response?.data?.message || t("Login.genericError"),
        );
        setOpen(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    try {
      const data = await ResendActivationAction(email);
      setResendMessage(data?.message || t("Login.activation.sent"));
    } catch (error) {
      setResendMessage(t("Login.activation.sent"));
    }
  };

  return (
    <div className="mt-8">
      {/* First, as on the signup page. Renders nothing until the API has
          Google's keys, and the form then starts the card on its own. "Keep me
          signed in" below still reaches it: the link carries the box's state
          at the moment it is pressed. */}
      <GoogleSignIn mode="login" rememberMe={rememberMe} divider="after" />

      <form onSubmit={handleSubmit}>
        <div className="form-item">
          <label htmlFor="login-email" className={labelClass}>
            {t("Login.email")}
          </label>
          <input
            type="email"
            id="login-email"
            name="email"
            className={fieldClass}
            placeholder={t("Login.email")}
            value={email}
            onChange={handleInputChange}
            autoComplete="email"
            required
          />
        </div>

        <div className="form-item mt-4">
          <div className="flex items-baseline justify-between">
            <label htmlFor="login-password" className={labelClass}>
              {t("Login.password")}
            </label>
            <Link to="/forgot-password" className={`${textLinkClass} mb-1.5 text-xs`}>
              {t("Login.forgotPassword")}
            </Link>
          </div>
          <input
            type="password"
            id="login-password"
            name="password"
            className={fieldClass}
            placeholder={t("Login.password")}
            value={password}
            onChange={handleInputChange}
            autoComplete="current-password"
            required
          />
        </div>

        <div className="form-item remember-me mt-3">
          <label
            htmlFor="login-remember"
            className="flex min-h-11 cursor-pointer items-center gap-2.5 text-fluid-sm font-medium text-ink-700"
          >
            <input
              type="checkbox"
              id="login-remember"
              name="rememberMe"
              className="size-4 shrink-0 cursor-pointer rounded border-ink-300 accent-brand-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
              checked={rememberMe}
              onChange={(event) => setRememberMe(event.target.checked)}
            />
            <span className="min-w-0">{t("Login.rememberMe")}</span>
          </label>
        </div>

        {needsActivation && (
          <div
            role="alert"
            className="activation-notice mt-5 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-fluid-sm text-ink-700"
          >
            <p className="font-semibold text-ink-900">
              {t("Login.activation.title")}
            </p>
            <p className="mt-1 leading-relaxed">
              {t("Login.activation.body", { email })}
            </p>

            {resendMessage ? (
              <p className="mt-2 text-xs text-ink-600">{resendMessage}</p>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                className={`${textLinkClass} mt-2 text-xs`}
              >
                {t("Login.activation.resend")}
              </button>
            )}
          </div>
        )}

        <div className="form-item mt-5">
          <button
            type="submit"
            className={primaryButtonClass}
            disabled={submitting}
          >
            {submitting ? t("Login.submitting") : t("Login.submit")}
          </button>
        </div>
      </form>

      <Toast
        open={open}
        message={errorMessage}
        onClose={handleClose}
        severity="error"
      />
    </div>
  );
};

export default Login;
